// Plain-language copy maps + helpers for the AI chat surface: they keep raw
// enums out of the DOM and hold the retryable/non-retryable policy. Keys mirror
// backend enums (UploadRejectionReason, error codes, AiCalculationJobStatus).

// Single source of truth lives with the enforcement layer (the hooks); re-exported here
// so the presentational chat copy/components import limits from one place.
export { maxNoteLength } from "../../../hooks/useAiDraftController";
export { maxFiles } from "../../../hooks/useFileUpload";

const rejectReasonCopy: Record<string, string> = {
  EMPTY: "The file is empty.",
  TOO_LARGE: "File exceeds the maximum allowed size.",
  INVALID_TYPE: "File type is not supported. Upload a PDF.",
  INFECTED: "The file failed the security scan.",
};

const statusCopy: Record<string, string> = {
  PENDING: "Getting started…",
  PARSING: "Reading your documents…",
  MAPPING: "AI is analysing the figures and building your draft…",
  READY: "Your draft is ready.",
  READY_NO_DATA: "We couldn't map the documents to this calculation.",
  FAILED: "Something went wrong while generating the draft.",
  STALE: "The request took too long. Please retry.",
};

// Maps a failed job's errorCode to a specific, actionable message. Anything not
// listed falls back to the generic FAILED copy.
const errorCodeCopy: Record<string, string> = {
  OPDATA_NOT_POPULATED:
    "Operational data for this line and year hasn't been set up yet, so we can't build the calculation. Add it under Operational data, then try again.",
  DOCUMENT_NOT_PARSED:
    "We couldn't read the uploaded document. Check it's a valid PDF and try again.",
  PARSE_UNREADABLE:
    "We couldn't read the uploaded document. Check it's a valid PDF and try again.",
  PARSE_TIMEOUT: "Reading the document took too long. Please try again.",
  PARSE_TOO_MANY_PAGES:
    "The document has too many pages to process. Upload a shorter document.",
  PARSE_FILE_TOO_LARGE:
    "The document is too large to process. Upload a smaller file.",
  MESSAGE_TOO_LONG: "Your note is too long. Shorten it and try again.",
  EXTRACTION_FAILED:
    "The AI couldn't finish generating the draft. Please try again.",
};

// Errors a plain retry can't fix — the user must change inputs or fix config.
const nonRetryableErrorCodes = new Set([
  "OPDATA_NOT_POPULATED",
  "MESSAGE_TOO_LONG",
  "PARSE_TOO_MANY_PAGES",
  "PARSE_FILE_TOO_LARGE",
  "DOCUMENT_NOT_PARSED",
  "PARSE_UNREADABLE",
]);

export function toRejectCopy(code: string | null): string {
  if (!code) return "File was rejected.";
  return rejectReasonCopy[code] ?? "File was rejected.";
}

export function toStatusCopy(status: string): string {
  return statusCopy[status] ?? "Processing…";
}

// Failed-state copy: prefer a specific message for a known errorCode, else the
// generic fallback. Callers may show the AI summary separately when no specific
// errorCode message applies.
export function toFailureCopy(errorCode: string | null): string {
  if (errorCode && errorCodeCopy[errorCode]) return errorCodeCopy[errorCode];
  return toStatusCopy("FAILED");
}

export function hasSpecificErrorCopy(errorCode: string | null): boolean {
  return !!errorCode && errorCode in errorCodeCopy;
}

export function isRetryable(errorCode: string | null): boolean {
  return !nonRetryableErrorCodes.has(errorCode ?? "");
}

// Reassurance shown under the live status while a draft generates: reading a
// PDF + the AI mapping call takes a little time (Textract + Bedrock), and a
// silent spinner reads as "hung". The hint escalates with elapsed time so it
// never goes stale: it states a typical upper bound up front, then acknowledges
// the overrun instead of leaving a now-false estimate on screen. The bound
// reflects measured latency — mapping ~13s, full parse+map+draft ~20s — so the
// common experience is well under the ceiling.
const progressTypicalSeconds = 45;

/**
 * The patience line under the live status, chosen by how long we've been
 * waiting:
 *  - first few seconds: no hint (the spinner alone is fine for a quick job);
 *  - up to the ceiling: state the typical wait so the user knows what to expect;
 *  - past the ceiling: acknowledge the overrun and reassure it's still working.
 */
export function progressPatienceHint(elapsedSeconds: number): string | null {
  if (elapsedSeconds < 5) return null;
  if (elapsedSeconds < progressTypicalSeconds) {
    return (
      "This usually takes under a minute — you can stay on this page, " +
      "we'll show your draft as soon as it's ready."
    );
  }
  return (
    "This is taking longer than usual, but it's still working — thanks for your " +
    "patience. Please keep this page open."
  );
}

// Elapsed time as compact "42s" / "1m 05s", so the user sees progress is still
// moving. Seconds are zero-padded once minutes appear.
export function formatElapsed(totalSeconds: number): string {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return `${minutes}m ${String(rest).padStart(2, "0")}s`;
}

// Empty-state welcome + suggestion cards (static this iteration; click-behaviour
// is a prefill only — full functionality is future work).
export const welcomeTitle = "TrackWise";
export const welcomeBody =
  "Upload the supplier price list or contract, and I'll draft this budget from it. Add a note for anything the documents don't cover.";

export const suggestionPrompts: string[] = [
  "Create a new budget for FLX10 line for 2026",
  "Create a Service Excellence budget for FLX10",
  "Create a Partner Manager budget for FLX10",
  "Create a Fleet Manager budget for FLX10",
];

export const composerPlaceholder = "Upload a file or give a prompt to start…";
